// One fetch of GET /getusersubscriptiondata per user, shared by every caller.
//
// Three independent places ask for this on a single dashboard pageload — the
// router's payment gate (src/router/index.ts), the plan-flag store
// (src/stores/planAccess.ts) and the profile menu (ProfileDD.vue) — and each one
// had its own cache, so none of them could see the others. Sentry
// BS-WEB-APP-16P caught the result: three consecutive identical GETs on the same
// pageload, the slowest 3.84s of a 15.2s load.
//
// Fixing it at the three call sites would not hold: the next component that
// needs the subscription shape adds a fourth. The dedupe lives here instead, and
// the call sites keep their own semantics on top of it. See utils/perUserCache
// for how the sharing works and what `maxAge: 0` means — the payment gate passes
// it so that paying unlocks the app on the next navigation rather than up to a
// TTL later.
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper';
import { createPerUserRequest } from '@/utils/perUserCache';

const TTL_MS = 30_000;

const shared = createPerUserRequest<any>((userId) => {
    const baseUrl = (import.meta as any).env?.VITE_API_URL_API || '/api';
    return fetchWrapper.get(`${baseUrl}/getusersubscriptiondata?user_id=${encodeURIComponent(userId)}`);
}, TTL_MS);

/**
 * Drop the cached subscription answer. Called on sign-out and account switch
 * (utils/resetPerAccountStores.ts, and the router's own reset). A different user
 * id resets it anyway — this covers the same id signing back in after a plan
 * change.
 */
export function resetSubscriptionData(): void {
    shared.reset();
}

export function fetchSubscriptionData(
    userId: number | string,
    opts: { maxAge?: number } = {}
): Promise<any> {
    return shared.fetch(userId, opts);
}
